import type { Table } from 'dexie';
import { db, type ReportRow, type ContactRow, type SyncOutboxRow } from '../db';
import { createClientConfigFromEnv, isSupabaseSyncEnabled } from '../supabase/config';
import { supabase } from '../supabase/browserClient';
import { SupabaseRestClient } from '../supabase/restClient';
import type { ContactSyncPayload } from '../personnel/contactSync';
import { ContactsRepo } from '../repos/contactsRepo';
import { getPendingSyncOperations, markSyncFailed, removeSyncedOperation } from './syncOutbox';
import { uploadReportAttachment } from './reportAttachments';
import { REPORT_ATTACHMENT_OUTBOX_TABLE } from './syncConstants';
import {
  markSyncStart,
  markSyncEnd,
  recordSyncConflict,
  recordSyncError,
  recordSyncLog,
  recordSyncSuccess,
} from './syncStatusStore';
import { recordContactConflict, type ContactConflict } from './contactConflicts';
import type { SyncError, SyncPullResult, SyncPushResult } from './syncTypes';

type SupabaseFilterValue = string | number | boolean | null | Array<string | number>;

type SyncTableAdapter<TRow> = {
  localName: string;
  remoteName: string;
  localTable: Table<TRow, string>;
  conflictTarget?: string;
  toRemote: (payload: unknown) => Record<string, unknown>;
  fromRemote: (remote: Record<string, unknown>) => TRow;
  getRemoteFilter: (payload: unknown, recordId: string) => Record<string, SupabaseFilterValue>;
  getLocalIdFromRemote: (remote: Record<string, unknown>) => string;
  mergeLocal?: (existing: TRow, incoming: TRow) => TRow;
};

const SYNC_CURSOR_PREFIX = 'ias.sync.cursor.';
const CURSOR_FALLBACK = new Map<string, string>();
let pushInFlight = false;
let pullInFlight = false;
const MAX_AUTO_RETRIES = 5;
const RETRYABLE_STATUS_CODES = new Set([408, 409, 429]);

function readSyncCursor(table: string): string | null {
  if (typeof localStorage === 'undefined') {
    return CURSOR_FALLBACK.get(table) ?? null;
  }
  return localStorage.getItem(`${SYNC_CURSOR_PREFIX}${table}`);
}

function writeSyncCursor(table: string, value: string): void {
  if (typeof localStorage === 'undefined') {
    CURSOR_FALLBACK.set(table, value);
    return;
  }
  localStorage.setItem(`${SYNC_CURSOR_PREFIX}${table}`, value);
}

function toSnakeCase(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function toCamelCase(value: string): string {
  return value.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

function mapToRemoteRecord(
  payload: unknown,
  options: {
    omit?: Set<string>;
    rename?: Record<string, string>;
    transform?: Record<string, (value: unknown) => unknown>;
  } = {}
): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const record = payload as Record<string, unknown>;
  const mapped: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (options.omit?.has(key)) continue;
    const mappedKey = options.rename?.[key] ?? toSnakeCase(key);
    const transformed = options.transform?.[key] ? options.transform[key](value) : value;
    mapped[mappedKey] = transformed;
  }

  return mapped;
}

const SYNC_METADATA_KEYS = new Set([
  'syncUpdatedAt',
  'syncDeletedAt',
  'syncDeviceId',
  'syncVersion',
  'createdAt',
  'updatedAt',
  'deletedAt',
]);

function mapRecordToRemoteWithPayload(
  payload: unknown,
  baseKeys: Set<string>,
  options: {
    omit?: Set<string>;
    rename?: Record<string, string>;
    transform?: Record<string, (value: unknown) => unknown>;
  } = {}
): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const record = payload as Record<string, unknown>;
  const base: Record<string, unknown> = {};
  const payloadData: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(record)) {
    if (SYNC_METADATA_KEYS.has(key)) continue;
    if (baseKeys.has(key)) {
      base[key] = value;
    } else if (value !== undefined) {
      payloadData[key] = value;
    }
  }

  const mapped = mapToRemoteRecord(base, options);
  mapped.payload = payloadData;
  return mapped;
}

function mergePayloadToLocal<T extends Record<string, unknown>>(mapped: T): T {
  if (!mapped.payload || typeof mapped.payload !== 'object') {
    return mapped;
  }
  const payload = mapped.payload as Record<string, unknown>;
  const { payload: removedPayload, ...rest } = mapped;
  void removedPayload;
  return { ...payload, ...rest } as T;
}

function mapToLocalRecord(
  payload: Record<string, unknown>,
  options: {
    omit?: Set<string>;
    rename?: Record<string, string>;
    transform?: Record<string, (value: unknown) => unknown>;
  } = {}
): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(payload)) {
    if (options.omit?.has(key)) continue;
    const mappedKey = options.rename?.[key] ?? toCamelCase(key);
    const transformed = options.transform?.[key] ? options.transform[key](value) : value;
    mapped[mappedKey] = transformed;
  }

  return mapped;
}

function mapContactPayload(payload: ContactSyncPayload): {
  id: string;
  company_id: string;
  site_id?: string | null;
  name: string;
  phone?: string | null;
  email?: string | null;
} {
  const { contact } = payload;
  return {
    id: contact.id,
    company_id: contact.companyId,
    site_id: contact.siteId ?? null,
    name: contact.name,
    phone: contact.phone ?? null,
    email: contact.email ?? null,
  };
}

function splitContactName(name: string): {
  firstName: string;
  lastName: string;
} {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: name.trim(), lastName: '' };
  }
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts.at(-1) ?? '',
  };
}

function normalizeReportPayload(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return value;
    }
  }
  return value;
}

function reportPayloadToString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value ?? {});
}

function extractSyncUpdatedAt(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const direct = record.syncUpdatedAt ?? record.sync_updated_at;
  return typeof direct === 'string' ? direct : null;
}

function extractRemoteSyncUpdatedAt(remote: Record<string, unknown>): string {
  const raw = remote.sync_updated_at ?? remote.syncUpdatedAt;
  if (typeof raw === 'string') return raw;
  return new Date().toISOString();
}

function isRemoteNewer(remote: string, local?: string | null): boolean {
  if (!local) return true;
  const remoteTime = Date.parse(remote);
  const localTime = Date.parse(local);
  if (!Number.isFinite(remoteTime) || !Number.isFinite(localTime)) {
    return false;
  }
  return remoteTime > localTime;
}

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown sync error';
  }
}

function getErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object') return null;
  const record = error as Record<string, unknown>;
  if (typeof record.status === 'number') return record.status;
  return null;
}

function isRetriableError(error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status === null) return true;
  if (status >= 500) return true;
  if (status >= 400 && status < 500) {
    return RETRYABLE_STATUS_CODES.has(status);
  }
  return true;
}

function computeRetryOutcome(
  retryCount: number,
  error: unknown
): { nextRetryCount: number; shouldRetry: boolean } {
  const nextRetryCount = retryCount + 1;
  if (!isRetriableError(error)) {
    return { nextRetryCount: MAX_AUTO_RETRIES, shouldRetry: false };
  }
  return {
    nextRetryCount,
    shouldRetry: nextRetryCount < MAX_AUTO_RETRIES,
  };
}

function computeBackoffDelay(retryCount: number): number {
  const base = 1000;
  const delay = base * Math.pow(2, retryCount);
  return Math.min(delay, 30000);
}

async function sleep(ms: number): Promise<void> {
  if (ms <= 0) return;
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function createSyncClient(): Promise<SupabaseRestClient | null> {
  const config = createClientConfigFromEnv();
  if (!config) return null;

  // Get the current user's session to use their JWT for authenticated requests
  let accessToken: string | undefined;
  if (supabase) {
    const { data } = await supabase.auth.getSession();
    accessToken = data.session?.access_token;
  }

  return new SupabaseRestClient({ ...config, accessToken });
}

// Templates are bundled with the app - no sync needed
const ACTIONS_REMOTE_OMIT = new Set(['reportTitle', 'reportVisitDate', 'reportSiteName']);
const COMPANY_BASE_KEYS = new Set(['id', 'name', 'code']);
const SITE_BASE_KEYS = new Set(['id', 'companyId', 'name']);
const UNIT_BASE_KEYS = new Set(['id', 'companyId', 'siteId', 'name']);
const ACTION_BASE_KEYS = new Set([
  'id',
  'companyId',
  'siteId',
  'reportId',
  'title',
  'type',
  'priority',
  'status',
  'dueDate',
]);

function recordPushSuccess(entry: SyncOutboxRow, message: string): void {
  recordSyncLog({
    phase: 'push',
    status: 'success',
    message,
    table: entry.table,
    recordId: entry.recordId,
    operation: entry.operation,
  });
}

const SYNC_TABLES: Array<SyncTableAdapter<unknown>> = [
  {
    localName: 'companies',
    remoteName: 'companies',
    localTable: db.companies,
    toRemote: (payload) =>
      mapRecordToRemoteWithPayload(payload, COMPANY_BASE_KEYS, {
        omit: new Set(['countryCode', 'createdBy']),
      }),
    fromRemote: (remote) =>
      mergePayloadToLocal(
        mapToLocalRecord(remote, {
          omit: new Set(['country_code', 'created_by']),
        })
      ),
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
  {
    localName: 'sites',
    remoteName: 'sites',
    localTable: db.sites,
    toRemote: (payload) =>
      mapRecordToRemoteWithPayload(payload, SITE_BASE_KEYS, {
        omit: new Set(['city', 'countryCode']),
      }),
    fromRemote: (remote) =>
      mergePayloadToLocal(
        mapToLocalRecord(remote, {
          omit: new Set(['city', 'country_code']),
        })
      ),
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
  {
    localName: 'units',
    remoteName: 'units',
    localTable: db.units,
    toRemote: (payload) => mapRecordToRemoteWithPayload(payload, UNIT_BASE_KEYS),
    fromRemote: (remote) => mergePayloadToLocal(mapToLocalRecord(remote)),
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
  {
    localName: 'contacts',
    remoteName: 'contacts',
    localTable: db.contacts,
    toRemote: (payload) => {
      if (!payload || typeof payload !== 'object') return {};
      const record = payload as Record<string, unknown>;
      const name =
        typeof record.firstName === 'string' || typeof record.lastName === 'string'
          ? `${String(record.firstName ?? '').trim()} ${String(
              record.lastName ?? ''
            ).trim()}`.trim()
          : String(record.name ?? '');
      return mapToRemoteRecord(
        {
          ...record,
          name,
        },
        {
          omit: new Set([
            'firstName',
            'lastName',
            'role',
            'scope',
            'reportRecipient',
            'alarmContact',
            'fireTraining',
            'firstAidTraining',
            'licensed',
            'licenseExpiry',
            'licenseLastUpdate',
            'reperibile',
            'deletedAt',
          ]),
        }
      );
    },
    fromRemote: (remote) => {
      const name = typeof remote.name === 'string' ? remote.name : '';
      const { firstName, lastName } = splitContactName(name);
      const mapped = mapToLocalRecord(remote, {
        omit: new Set(['name']),
      });
      return {
        ...mapped,
        firstName,
        lastName,
      } as unknown as ContactRow;
    },
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
    mergeLocal: (existing, incoming) => {
      const next = { ...(existing as Record<string, unknown>) };
      const incomingRecord = incoming as Record<string, unknown>;
      for (const [key, value] of Object.entries(incomingRecord)) {
        if (value !== undefined) {
          next[key] = value;
        }
      }
      return next as unknown as ContactRow;
    },
  },
  {
    localName: 'reports',
    remoteName: 'reports',
    localTable: db.reports,
    toRemote: (payload) =>
      mapToRemoteRecord(payload, {
        rename: {
          companyId: 'org_id', // Database uses org_id, not company_id
        },
        transform: {
          payload: normalizeReportPayload,
        },
      }),
    fromRemote: (remote) =>
      ({
        ...mapToLocalRecord(remote, {
          rename: {
            org_id: 'companyId', // Map back from org_id to companyId
          },
          transform: {
            payload: reportPayloadToString,
          },
        }),
      }) as unknown as ReportRow,
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
  {
    localName: 'actions',
    remoteName: 'alerts', // Supabase table is named "alerts"
    localTable: db.actions,
    toRemote: (payload) =>
      mapRecordToRemoteWithPayload(payload, ACTION_BASE_KEYS, {
        omit: ACTIONS_REMOTE_OMIT,
      }),
    fromRemote: (remote) => mergePayloadToLocal(mapToLocalRecord(remote)),
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
  // Templates are bundled with the app and read-only - no sync needed
  // {
  //   localName: "templates",
  //   remoteName: "templates",
  //   ...
  // },
  {
    localName: 'componentCategories',
    remoteName: 'component_categories',
    localTable: db.componentCategories,
    toRemote: (payload) => mapToRemoteRecord(payload),
    fromRemote: (remote) => mapToLocalRecord(remote),
    getRemoteFilter: (_payload, recordId) => ({ id: recordId }),
    getLocalIdFromRemote: (remote) => String(remote.id),
  },
];

function getAdapter(tableName: string): SyncTableAdapter<unknown> | undefined {
  return SYNC_TABLES.find((table) => table.localName === tableName);
}

export async function pushOutbox(
  providedClient?: SupabaseRestClient | null,
  options: { force?: boolean } = {}
): Promise<SyncPushResult> {
  if (pushInFlight) {
    return { status: 'ok', processed: 0, conflicts: 0, failed: 0, errors: [] };
  }
  if (!isSupabaseSyncEnabled()) {
    return {
      status: 'disabled',
      processed: 0,
      conflicts: 0,
      failed: 0,
      errors: [],
    };
  }

  const client = providedClient ?? (await createSyncClient());
  if (!client) {
    return {
      status: 'unconfigured',
      processed: 0,
      conflicts: 0,
      failed: 0,
      errors: [
        {
          phase: 'push',
          table: 'supabase',
          message: 'Supabase non configurato: impossibile avviare la sync.',
        },
      ],
    };
  }

  pushInFlight = true;
  markSyncStart();

  const entries = await getPendingSyncOperations();
  const errors: SyncError[] = [];
  let processed = 0;
  let conflicts = 0;
  let failed = 0;
  const force = options.force ?? false;

  try {
    for (const entry of entries) {
      if (!force && entry.retryCount >= MAX_AUTO_RETRIES) {
        continue;
      }
      if (entry.table === REPORT_ATTACHMENT_OUTBOX_TABLE) {
        const delay = force ? 0 : computeBackoffDelay(entry.retryCount);
        await sleep(delay);

        try {
          await uploadReportAttachment(
            entry.payload as unknown as {
              reportId: string;
              mediaId: string;
              storagePath: string;
              mimeType?: string;
              byteSize?: number;
            }
          );
          await removeSyncedOperation(entry.id);
          processed += 1;
          recordPushSuccess(entry, 'Media sincronizzato');
        } catch (error) {
          failed += 1;
          const message = formatErrorMessage(error);
          const retryOutcome = computeRetryOutcome(entry.retryCount, error);
          await markSyncFailed(entry.id, message, {
            retryCount: retryOutcome.nextRetryCount,
          });
          const syncError: SyncError = {
            phase: 'push',
            table: entry.table,
            recordId: entry.recordId,
            operation: entry.operation,
            message,
            cause: error,
          };
          errors.push(syncError);
          recordSyncError(syncError);
        }
        continue;
      }

      if (entry.table === 'contacts') {
        const delay = force ? 0 : computeBackoffDelay(entry.retryCount);
        await sleep(delay);

        try {
          const payload = entry.payload as ContactSyncPayload;
          if (!payload?.contact?.id || !payload.contact.companyId) {
            throw new Error('Payload contatto non valido.');
          }

          const remotePayload = mapContactPayload(payload);

          const idempotencyHeaders = entry.idempotencyKey
            ? { 'Idempotency-Key': entry.idempotencyKey }
            : undefined;

          if (entry.operation === 'delete') {
            await client.delete('contacts', {
              filters: { id: remotePayload.id },
              headers: idempotencyHeaders,
            });
          } else if (payload.expectedUpdatedAt) {
            const updated = await client.update<Record<string, unknown>>(
              'contacts',
              remotePayload,
              {
                filters: {
                  id: remotePayload.id,
                  updated_at: payload.expectedUpdatedAt,
                },
                headers: idempotencyHeaders,
              }
            );

            if (updated.length === 0) {
              const remoteRows = await client.select<Record<string, unknown>>('contacts', {
                columns: 'id,name,phone,email,updated_at,site_id',
                filters: { id: remotePayload.id },
                limit: 1,
              });

              const remote = remoteRows[0];
              if (!remote) {
                await client.upsert('contacts', remotePayload, {
                  onConflict: 'id',
                  headers: idempotencyHeaders,
                });
              } else {
                conflicts += 1;
                const conflict: ContactConflict = {
                  id: remotePayload.id,
                  contactId: remotePayload.id,
                  companyId: payload.contact.companyId,
                  siteId:
                    typeof remote.site_id === 'string' ? remote.site_id : payload.contact.siteId,
                  local: {
                    name: payload.contact.name,
                    phone: payload.contact.phone ?? undefined,
                    email: payload.contact.email ?? undefined,
                  },
                  remote: {
                    name: typeof remote.name === 'string' ? remote.name : '—',
                    phone: typeof remote.phone === 'string' ? remote.phone : undefined,
                    email: typeof remote.email === 'string' ? remote.email : undefined,
                  },
                  remoteUpdatedAt: typeof remote.updated_at === 'string' ? remote.updated_at : null,
                  createdAt: new Date().toISOString(),
                };
                await recordContactConflict(conflict);
                const syncError: SyncError = {
                  phase: 'push',
                  table: entry.table,
                  recordId: entry.recordId,
                  operation: entry.operation,
                  message: 'Conflitto contatto: il record remoto è cambiato, revisione necessaria.',
                };
                errors.push(syncError);
                recordSyncConflict(syncError);
                await removeSyncedOperation(entry.id);
                continue;
              }
            } else {
              const remoteUpdatedAt =
                typeof updated[0]?.updated_at === 'string' ? String(updated[0].updated_at) : null;
              if (remoteUpdatedAt) {
                await ContactsRepo.applyContactSyncUpdate(payload.contact.id, {
                  updatedAt: remoteUpdatedAt,
                });
              }
            }
          } else {
            const upserted = await client.upsert<Record<string, unknown>>(
              'contacts',
              remotePayload,
              { onConflict: 'id', headers: idempotencyHeaders }
            );
            const remoteUpdatedAt =
              typeof upserted[0]?.updated_at === 'string' ? String(upserted[0].updated_at) : null;
            if (remoteUpdatedAt) {
              await ContactsRepo.applyContactSyncUpdate(payload.contact.id, {
                updatedAt: remoteUpdatedAt,
              });
            }
          }

          await removeSyncedOperation(entry.id);
          processed += 1;
          recordPushSuccess(entry, 'Contatto sincronizzato');
        } catch (error) {
          failed += 1;
          const message = formatErrorMessage(error);
          const retryOutcome = computeRetryOutcome(entry.retryCount, error);
          await markSyncFailed(entry.id, message, {
            retryCount: retryOutcome.nextRetryCount,
          });
          const syncError: SyncError = {
            phase: 'push',
            table: entry.table,
            recordId: entry.recordId,
            operation: entry.operation,
            message,
            cause: error,
          };
          errors.push(syncError);
          recordSyncError(syncError);
        }
        continue;
      }

      const adapter = getAdapter(entry.table);
      if (!adapter) {
        failed += 1;
        errors.push({
          phase: 'push',
          table: entry.table,
          recordId: entry.recordId,
          operation: entry.operation,
          message: 'Tabella non supportata dal motore di sync.',
        });
        await markSyncFailed(entry.id, 'Unsupported table', {
          retryCount: MAX_AUTO_RETRIES,
        });
        continue;
      }

      const delay = force ? 0 : computeBackoffDelay(entry.retryCount);
      await sleep(delay);

      try {
        const remoteFilter = adapter.getRemoteFilter(entry.payload, entry.recordId);
        const remoteRows = await client.select<Record<string, unknown>>(adapter.remoteName, {
          columns: 'sync_updated_at,sync_deleted_at',
          filters: remoteFilter,
          limit: 1,
        });
        const remote = remoteRows[0];
        const localUpdatedAt = extractSyncUpdatedAt(entry.payload);
        const remoteUpdatedAt = remote ? extractRemoteSyncUpdatedAt(remote) : null;

        if (remoteUpdatedAt && isRemoteNewer(remoteUpdatedAt, localUpdatedAt)) {
          conflicts += 1;
          const conflict: SyncError = {
            phase: 'push',
            table: entry.table,
            recordId: entry.recordId,
            operation: entry.operation,
            message: 'Record remoto più recente: aggiornamento locale scartato.',
          };
          errors.push(conflict);
          recordSyncConflict(conflict);
          await removeSyncedOperation(entry.id);
          continue;
        }

        const payload = adapter.toRemote(entry.payload);
        await client.upsert(adapter.remoteName, payload, {
          onConflict: adapter.conflictTarget ?? 'id',
          headers: entry.idempotencyKey ? { 'Idempotency-Key': entry.idempotencyKey } : undefined,
        });

        await removeSyncedOperation(entry.id);
        processed += 1;
        recordPushSuccess(entry, 'Record sincronizzato');
      } catch (error) {
        failed += 1;
        const message = formatErrorMessage(error);
        const retryOutcome = computeRetryOutcome(entry.retryCount, error);
        await markSyncFailed(entry.id, message, {
          retryCount: retryOutcome.nextRetryCount,
        });
        const syncError: SyncError = {
          phase: 'push',
          table: entry.table,
          recordId: entry.recordId,
          operation: entry.operation,
          message,
          cause: error,
        };
        errors.push(syncError);
        recordSyncError(syncError);
      }
    }
  } finally {
    pushInFlight = false;
  }

  if (errors.length === 0 && conflicts === 0) {
    recordSyncSuccess('push', 'Sync push completata');
  } else {
    markSyncEnd();
  }

  return { status: 'ok', processed, conflicts, failed, errors };
}

export async function pullUpdates(
  providedClient?: SupabaseRestClient | null
): Promise<SyncPullResult> {
  if (pullInFlight) {
    return { status: 'ok', applied: 0, skipped: 0, conflicts: 0, errors: [] };
  }
  if (!isSupabaseSyncEnabled()) {
    return {
      status: 'disabled',
      applied: 0,
      skipped: 0,
      conflicts: 0,
      errors: [],
    };
  }

  const client = providedClient ?? (await createSyncClient());
  if (!client) {
    return {
      status: 'unconfigured',
      applied: 0,
      skipped: 0,
      conflicts: 0,
      errors: [
        {
          phase: 'pull',
          table: 'supabase',
          message: 'Supabase non configurato: impossibile avviare la sync.',
        },
      ],
    };
  }

  pullInFlight = true;
  markSyncStart();

  const errors: SyncError[] = [];
  let applied = 0;
  let skipped = 0;
  let conflicts = 0;

  try {
    for (const adapter of SYNC_TABLES) {
      const cursor = readSyncCursor(adapter.localName);
      const filters: Record<string, SupabaseFilterValue> | undefined = cursor
        ? { sync_updated_at: `gt.${cursor}` }
        : undefined;

      let latestCursor = cursor;

      try {
        const remoteRows = await client.select<Record<string, unknown>>(adapter.remoteName, {
          filters,
          order: { column: 'sync_updated_at', ascending: true },
        });

        for (const remote of remoteRows) {
          const remoteUpdatedAt = extractRemoteSyncUpdatedAt(remote);
          const localId = adapter.getLocalIdFromRemote(remote);
          const local = await adapter.localTable.get(localId);
          const localUpdatedAt =
            local && typeof (local as { syncUpdatedAt?: string }).syncUpdatedAt === 'string'
              ? (local as { syncUpdatedAt?: string }).syncUpdatedAt
              : null;

          if (!isRemoteNewer(remoteUpdatedAt, localUpdatedAt)) {
            skipped += 1;
            latestCursor =
              !latestCursor || isRemoteNewer(remoteUpdatedAt, latestCursor)
                ? remoteUpdatedAt
                : latestCursor;
            continue;
          }

          const incoming = adapter.fromRemote(remote);
          const next = local
            ? (adapter.mergeLocal?.(local, incoming) ?? {
                ...(local as Record<string, unknown>),
                ...(incoming as Record<string, unknown>),
              })
            : incoming;

          await adapter.localTable.put(next);
          applied += 1;
          latestCursor =
            !latestCursor || isRemoteNewer(remoteUpdatedAt, latestCursor)
              ? remoteUpdatedAt
              : latestCursor;
        }
      } catch (error) {
        const syncError: SyncError = {
          phase: 'pull',
          table: adapter.localName,
          message: formatErrorMessage(error),
          cause: error,
        };
        errors.push(syncError);
        recordSyncError(syncError);
      }

      if (latestCursor && latestCursor !== cursor) {
        writeSyncCursor(adapter.localName, latestCursor);
      }
    }
  } finally {
    pullInFlight = false;
  }

  if (errors.length === 0) {
    recordSyncSuccess('pull', 'Sync pull completata');
  } else {
    markSyncEnd();
  }

  return { status: 'ok', applied, skipped, conflicts, errors };
}

/**
 * Lightweight freshness check that queries only max(sync_updated_at) for each table.
 * Used for 30-second polling to avoid expensive full pulls when nothing changed.
 *
 * @returns true if remote has newer data than local cursors
 */
export async function checkRemoteFreshness(): Promise<boolean> {
  if (!isSupabaseSyncEnabled()) {
    return false;
  }

  const client = await createSyncClient();
  if (!client) {
    return false;
  }

  try {
    // Check each sync table for newer data than our cursor
    for (const adapter of SYNC_TABLES) {
      const localCursor = readSyncCursor(adapter.localName);

      // Query only the most recent record's sync_updated_at
      const rows = await client.select<Record<string, unknown>>(adapter.remoteName, {
        columns: 'sync_updated_at',
        order: { column: 'sync_updated_at', ascending: false },
        limit: 1,
      });

      const remoteLatest = rows[0]?.sync_updated_at;
      if (typeof remoteLatest === 'string') {
        if (!localCursor || isRemoteNewer(remoteLatest, localCursor)) {
          return true;
        }
      }
    }

    return false;
  } catch {
    // On error, assume we should pull to be safe
    return true;
  }
}
